import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress } from '@mui/material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';

/**
 * Pick an ERP record by NAME, never by id.
 *
 * The pattern this replaces: a "Related Entity Type" dropdown next to a
 * free-text "Entity ID" box with helper text like "Numeric ID of the lead /
 * opportunity / customer". That asks a salesperson to go and find a database
 * primary key and type it back in — and there is nothing to stop them typing
 * the wrong one, because a number is always "valid".
 *
 * It searches the existing `/global-search` endpoint (the same index behind
 * the Ctrl+K palette) and filters to the requested entity type, so it works
 * for any type that search already covers without a new endpoint per screen.
 *
 * The value handed back is still the id — that is what the API wants — but the
 * user only ever sees and chooses a name.
 */

export type PickerEntityType =
  | 'lead' | 'opportunity' | 'customer' | 'contact' | 'supplier' | 'material'
  | 'sales_order' | 'purchase_order' | 'sales_invoice' | 'purchase_invoice'
  | 'production_order' | 'journal' | 'employee' | 'lot';

interface SearchHit { type: string; id: number; label: string; sub?: string }

export interface EntityPickerProps {
  entityType: PickerEntityType | 'none' | '';
  value: number | string | null;
  onChange: (id: number | null, hit: SearchHit | null) => void;
  label?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** Placeholder shown before the user types. */
  placeholder?: string;
}

const TYPE_LABEL: Record<string, string> = {
  lead: 'Lead',
  opportunity: 'Opportunity',
  customer: 'Customer',
  contact: 'Contact',
  supplier: 'Vendor',
  material: 'Product',
  sales_order: 'Sales order',
  purchase_order: 'Purchase order',
  sales_invoice: 'Sales invoice',
  purchase_invoice: 'Purchase invoice',
  production_order: 'Production order',
  journal: 'Journal entry',
  employee: 'Employee',
  lot: 'Batch / lot',
};

export const EntityPicker: React.FC<EntityPickerProps> = ({
  entityType, value, onChange, label, helperText, required, disabled,
  size = 'small', fullWidth = true, placeholder,
}) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SearchHit | null>(null);

  const inactive = !entityType || entityType === 'none';

  // Changing the entity type invalidates any previous choice — a lead id is
  // not an opportunity id, and silently keeping it is how a task ends up
  // linked to the wrong record.
  useEffect(() => {
    setSelected(null);
    setOptions([]);
    setInput('');
    if (value) onChange(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  useEffect(() => {
    if (inactive || input.trim().length < 2) { setOptions([]); return undefined; }
    setLoading(true);
    const timer = setTimeout(() => {
      financeWorkspaceService.globalSearch(input.trim())
        .then((r) => {
          const hits: SearchHit[] = (r.results || []) as SearchHit[];
          setOptions(hits.filter((h) => h.type === entityType));
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [input, entityType, inactive]);

  const noun = useMemo(
    () => (inactive ? 'record' : (TYPE_LABEL[entityType as string] || entityType).toLowerCase()),
    [entityType, inactive]
  );

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || inactive}
      options={options}
      value={selected}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (o ? o.label : '')}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v, reason) => { if (reason === 'input') setInput(v); }}
      onChange={(_, v) => { setSelected(v); onChange(v ? v.id : null, v); }}
      noOptionsText={
        input.trim().length < 2
          ? `Type at least two characters to search ${noun}s`
          : `No matching ${noun} found`
      }
      renderOption={(props, o) => {
        const { key, ...rest } = props as any;
        return (
          <li key={o.id} {...rest}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>{o.label}</Typography>
              {o.sub && <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{o.sub}</Typography>}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || (inactive ? 'Related record' : TYPE_LABEL[entityType as string] || 'Record')}
          required={required}
          placeholder={placeholder || (inactive ? 'Choose a type first' : `Search ${noun}s by name…`)}
          helperText={helperText ?? (inactive ? ' ' : `Search by name — the id is looked up for you.`)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={15} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default EntityPicker;
